import { reactive } from 'vue';

export type MessageType =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'loading'
  | 'normal';

export interface MessageConfig {
  content: string;
  duration?: number;
  id?: string;
  closable?: boolean;
  onClose?: () => void;
}

export interface ToastItem {
  id: string;
  type: MessageType;
  content: string;
  duration: number;
  open: boolean;
  onClose?: () => void;
}

let counter = 0;

export const toastState = reactive<{ items: ToastItem[] }>({ items: [] });

function remove(id: string) {
  const index = toastState.items.findIndex((item) => item.id === id);
  if (index !== -1) toastState.items.splice(index, 1);
}

function add(
  type: MessageType,
  config: string | MessageConfig
): { close: () => void } {
  const cfg: MessageConfig =
    typeof config === 'string' ? { content: config } : config;

  // eslint-disable-next-line no-plusplus
  const id = cfg.id ?? `__toast_${++counter}`;
  const duration = cfg.duration ?? 3000;

  const existing = toastState.items.find((item) => item.id === id);
  if (existing) {
    existing.content = cfg.content;
    existing.duration = duration;
    existing.open = true;
    return { close: () => remove(id) };
  }

  toastState.items.push({
    id,
    type,
    content: cfg.content,
    duration,
    open: true,
    onClose: cfg.onClose,
  });

  return { close: () => remove(id) };
}

const Message = {
  success: (config: string | MessageConfig) => add('success', config),
  error: (config: string | MessageConfig) => add('error', config),
  warning: (config: string | MessageConfig) => add('warning', config),
  info: (config: string | MessageConfig) => add('info', config),
  loading: (config: string | MessageConfig) => add('loading', config),
  normal: (config: string | MessageConfig) => add('normal', config),
  clear: (_position?: string) => {
    toastState.items.splice(0);
  },
};

export default Message;
